import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { MapPin, Plus, Search, Edit, Trash2, Navigation, ExternalLink, Map, List, Grid3X3 } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { LocationMap } from '../../components/ui/location-map';

const LocationsPage = () => {
  const { plants, fetchPlants, fetchLocations } = useData();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  
  const [formData, setFormData] = useState({
    name: '',
    plant_id: '',
    description: '',
    latitude: '',
    longitude: '',
    icon: 'default'
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await fetchPlants();
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [fetchPlants]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (editLocation) {
        await api.put(`/locations/${editLocation.id}`, payload);
        toast.success('Locatie actualizata!');
      } else {
        await api.post('/locations', payload);
        toast.success('Locatie creata!');
      }
      setShowAddDialog(false);
      setEditLocation(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (location) => {
    setEditLocation(location);
    setFormData({
      name: location.name || '',
      plant_id: location.plant_id || '',
      description: location.description || '',
      latitude: location.latitude || '',
      longitude: location.longitude || '',
      icon: location.icon || 'default'
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta locatie?')) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success('Locatie stearsa!');
      loadData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      plant_id: '',
      description: '',
      latitude: '',
      longitude: '',
      icon: 'default'
    });
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const getPlantName = (plantId) => {
    const plant = plants.find(p => p.id === plantId);
    return plant?.name || 'N/A';
  };

  // Prepare data for DataTable
  const locationsWithPlantName = locations.map(loc => ({
    ...loc,
    plant_name: getPlantName(loc.plant_id),
    has_gps: loc.latitude && loc.longitude ? 'Da' : 'Nu',
    icon_type: loc.icon || 'default'
  }));

  const locationsWithGPS = locations.filter(l => l.latitude && l.longitude);

  // Columns for DataTable
  const tableColumns = [
    {
      key: 'name',
      label: 'Nume',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="text-lg">
            {row.icon === 'factory' ? '🏭' : 
             row.icon === 'warehouse' ? '🏢' :
             row.icon === 'office' ? '🏬' :
             row.icon === 'production' ? '⚙️' : '📍'}
          </span>
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      key: 'plant_name',
      label: 'Sediu',
      sortable: true,
      filterable: true
    },
    {
      key: 'description',
      label: 'Descriere',
      sortable: false,
      filterable: true,
      render: (value) => (
        <span className="text-slate-500 truncate max-w-[200px] block">{value || '-'}</span>
      )
    },
    {
      key: 'has_gps',
      label: 'GPS',
      sortable: true,
      filterable: true,
      width: 'w-20',
      render: (value, row) => (
        value === 'Da' ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openGoogleMaps(row.latitude, row.longitude);
            }}
            className="flex items-center gap-1 text-green-600 hover:text-green-700"
          >
            <Navigation className="w-4 h-4" />
            <span className="text-xs">{parseFloat(row.latitude).toFixed(2)}</span>
          </button>
        ) : (
          <span className="text-slate-400">-</span>
        )
      )
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      filterable: false,
      width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row)}
            data-testid={`edit-location-${row.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-700"
            data-testid={`delete-location-${row.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Locatii Echipamente</h1>
          <p className="text-slate-600 mt-1">Gestioneaza locatiile din sedii ({locations.length} total, {locationsWithGPS.length} cu GPS)</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) { setEditLocation(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12" data-testid="add-location-btn">
              <Plus className="w-5 h-5 mr-2" />
              LOCATIE NOUA
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editLocation ? 'Editeaza Locatie' : 'Adauga Locatie'}</DialogTitle>
              <DialogDescription>
                Completati informatiile pentru locatie
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nume *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-11"
                  placeholder="ex: Hala Productie"
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Sediu *</Label>
                <Select
                  value={formData.plant_id}
                  onValueChange={(value) => setFormData({ ...formData, plant_id: value })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Selecteaza sediul" />
                  </SelectTrigger>
                  <SelectContent>
                    {plants.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Descriere optionala"
                />
              </div>
              
              {/* GPS Coordinates */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-[#0052CC]" />
                  <Label className="font-medium">Coordonate GPS</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Latitudine</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      className="h-10"
                      placeholder="44.4268"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Longitudine</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      className="h-10"
                      placeholder="26.1025"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Exemplu: Bucuresti (44.4268, 26.1025)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Icon harta</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">📍 Default</SelectItem>
                    <SelectItem value="factory">🏭 Productie</SelectItem>
                    <SelectItem value="warehouse">🏢 Depozit</SelectItem>
                    <SelectItem value="office">🏬 Birou</SelectItem>
                    <SelectItem value="production">⚙️ Productie</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Anuleaza
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-location-btn">
                  {editLocation ? 'ACTUALIZEAZA' : 'SALVEAZA'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs pentru diferite vizualizari */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3 h-11">
          <TabsTrigger value="list" className="flex items-center gap-2" data-testid="tab-list">
            <List className="w-4 h-4" />
            Tabel
          </TabsTrigger>
          <TabsTrigger value="cards" className="flex items-center gap-2" data-testid="tab-cards">
            <Grid3X3 className="w-4 h-4" />
            Carduri
          </TabsTrigger>
          <TabsTrigger value="map" className="flex items-center gap-2" data-testid="tab-map">
            <Map className="w-4 h-4" />
            Harta
          </TabsTrigger>
        </TabsList>

        {/* Tab Tabel cu DataTable avansat */}
        <TabsContent value="list" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
            </div>
          ) : (
            <DataTable
              data={locationsWithPlantName}
              columns={tableColumns}
              pageSize={10}
              searchable={true}
              exportable={true}
              printable={true}
              selectable={true}
              onRowClick={handleEdit}
              emptyMessage="Nu exista locatii inregistrate"
            />
          )}
        </TabsContent>

        {/* Tab Carduri grupate pe sedii */}
        <TabsContent value="cards" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
            </div>
          ) : locations.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(
                locations.reduce((acc, loc) => {
                  const plantName = getPlantName(loc.plant_id);
                  if (!acc[plantName]) acc[plantName] = [];
                  acc[plantName].push(loc);
                  return acc;
                }, {})
              ).map(([plantName, locs]) => (
                <div key={plantName}>
                  <h2 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    🏭 {plantName}
                    <Badge variant="secondary">{locs.length}</Badge>
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {locs.map((location) => (
                      <Card key={location.id} className="card-industrial hover:shadow-md transition-shadow" data-testid={`location-card-${location.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-lg">
                                {location.icon === 'factory' ? '🏭' : 
                                 location.icon === 'warehouse' ? '🏢' :
                                 location.icon === 'office' ? '🏬' :
                                 location.icon === 'production' ? '⚙️' : '📍'}
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-900">{location.name}</h3>
                                {location.description && (
                                  <p className="text-sm text-slate-500 mt-1">{location.description}</p>
                                )}
                                {location.latitude && location.longitude && (
                                  <button
                                    onClick={() => openGoogleMaps(location.latitude, location.longitude)}
                                    className="flex items-center gap-1 text-xs text-[#0052CC] mt-2 hover:underline"
                                  >
                                    <Navigation className="w-3 h-3" />
                                    {parseFloat(location.latitude).toFixed(4)}, {parseFloat(location.longitude).toFixed(4)}
                                    <ExternalLink className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(location)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(location.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card className="card-industrial">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <MapPin className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500">Nu exista locatii</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab Harta cu Leaflet */}
        <TabsContent value="map" className="mt-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Map className="w-5 h-5 text-[#0052CC]" />
                Vizualizare Harta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LocationMap
                locations={locationsWithPlantName}
                height="500px"
                onMarkerClick={handleMarkerClick}
                selectedLocationId={selectedLocation?.id}
              />
              
              {/* Detalii locatie selectata */}
              {selectedLocation && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{selectedLocation.name}</h3>
                      <p className="text-sm text-slate-600">{getPlantName(selectedLocation.plant_id)}</p>
                      {selectedLocation.description && (
                        <p className="text-sm text-slate-500 mt-1">{selectedLocation.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(selectedLocation)}>
                        <Edit className="w-4 h-4 mr-1" /> Editeaza
                      </Button>
                      {selectedLocation.latitude && selectedLocation.longitude && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openGoogleMaps(selectedLocation.latitude, selectedLocation.longitude)}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" /> Google Maps
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LocationsPage;
